import { useEffect, useState } from "react";
import { createSeason, updateSeason, getTournamentTypes } from "../../api/seasons";
import { ISeason, ITournamentType } from "../../models/ISeason";
import moment from "moment";
import { Alert, Button, Dialog, Field, FormGrid, Input, Select, Switch, DatePicker } from "../../ui";
import "./styles.scss";

type SeasonDialogProps = {
  season?: ISeason;
  isOpen: boolean;
  onClose: (update?: boolean) => void;
};

const SeasonDialog = (props: SeasonDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [tournamentTypes, setTournamentTypes] = useState<ITournamentType[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    typeId: "",
    startDate: null as Date | null,
    endDate: null as Date | null,
    isActive: true,
  });

  useEffect(() => {
    if (props.isOpen) {
      loadTournamentTypes();

      if (props.season) {
        setFormData({
          name: props.season.name,
          typeId: props.season.typeId,
          startDate: props.season.startDate ? new Date(props.season.startDate) : null,
          endDate: props.season.endDate ? new Date(props.season.endDate) : null,
          isActive: props.season.isActive,
        });
      } else {
        setFormData({
          name: "",
          typeId: "",
          startDate: null,
          endDate: null,
          isActive: true,
        });
      }
    }
  }, [props.isOpen, props.season]);

  const loadTournamentTypes = async () => {
    setLoadingTypes(true);
    try {
      const response = await getTournamentTypes();
      setTournamentTypes(response.data);
    } catch (error: any) {
      console.error("Error loading tournament types:", error);
      setErrorMessage(error?.message || "Failed to load tournament types");
    } finally {
      setLoadingTypes(false);
    }
  };

  const handleClose = (update?: boolean) => {
    props.onClose(update);
    setErrorMessage("");
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.name.trim()) {
      setErrorMessage("Season name is required");
      return;
    }

    if (!formData.typeId) {
      setErrorMessage("Tournament type is required");
      return;
    }

    // Validate dates if both are provided
    if (formData.startDate && formData.endDate) {
      if (formData.startDate > formData.endDate) {
        setErrorMessage("Start date must be before end date");
        return;
      }
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const payload = {
        name: formData.name.trim(),
        typeId: formData.typeId,
        startDate: formData.startDate ? moment(formData.startDate).utc().format("YYYY-MM-DDTHH:mm:ss[Z]") : null,
        endDate: formData.endDate ? moment(formData.endDate).utc().endOf("day").format("YYYY-MM-DDTHH:mm:ss[Z]") : null,
        isActive: formData.isActive,
      };

      if (props.season) {
        // Update existing season
        await updateSeason(props.season.id, payload);
      } else {
        // Create new season
        await createSeason(payload);
      }
      handleClose(true);
    } catch (error: any) {
      setErrorMessage(error?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={props.isOpen}
      onClose={() => handleClose()}
      title={props.season ? "Edit Season" : "Create Season"}
      busy={loading}
      bodyClassName="season-dialog__body"
      footer={
        <>
          <Button variant="ghost" onClick={() => handleClose()} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading} disabled={loadingTypes}>
            {props.season ? "Update" : "Create"}
          </Button>
        </>
      }
    >
      {errorMessage && <Alert tone="danger">{errorMessage}</Alert>}

      <Field label="Season Name" required>
        {(id) => (
          <Input
            id={id}
            type="text"
            placeholder="e.g., Season 1 - Volleyball"
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            disabled={loading}
          />
        )}
      </Field>

      <Field label="Tournament Type" required>
        {(id) => (
          <Select
            id={id}
            block
            value={formData.typeId}
            onChange={(e) => handleChange("typeId", e.target.value)}
            disabled={loading || loadingTypes}
          >
            <option value="">Select a tournament type</option>
            {tournamentTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.typeName}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <FormGrid columns={2}>
        <Field label="Start Date (Optional)">
          {(id) => (
            <DatePicker
              id={id}
              selected={formData.startDate}
              onChange={(date: Date | null) => handleChange("startDate", date)}
              dateFormat="dd MMM yyyy"
              placeholderText="Select start date"
              disabled={loading}
              maxDate={formData.endDate || undefined}
            />
          )}
        </Field>

        <Field label="End Date (Optional)">
          {(id) => (
            <DatePicker
              id={id}
              selected={formData.endDate}
              onChange={(date: Date | null) => handleChange("endDate", date)}
              dateFormat="dd MMM yyyy"
              placeholderText="Select end date"
              disabled={loading}
              minDate={formData.startDate || undefined}
            />
          )}
        </Field>
      </FormGrid>

      <Switch
        checked={formData.isActive}
        onChange={(checked) => handleChange("isActive", checked)}
        label="Active"
        disabled={loading}
      />
    </Dialog>
  );
};

export default SeasonDialog;
